import React, { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";
import { Shuffle, Sparkles, Grid3x3, X, Search } from "lucide-react";
import { pad, num } from "@/lib/format";
import { toast } from "sonner";

const BLOCKS = [10, 25, 50, 100, 250, 500, 1000];

export default function TicketSelector({ raffle, taken, selected, setSelected }) {
  const [mode, setMode] = useState("manual");
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [spinning, setSpinning] = useState(false);
  const perPage = 100;
  const takenSet = useMemo(() => new Set(taken), [taken]);
  const total = raffle.total_tickets;
  const first = raffle.number_start ?? 1;
  const last = first + total - 1;
  const len = Math.max(2, String(last).length);
  const fmt = (n) => pad(n, len);

  const toggle = (n) => {
    if (takenSet.has(n)) { toast.error(`Boleto ${fmt(n)} ya no disponible`); return; }
    setSelected((prev) => prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]);
  };

  const pickRandom = (count) => {
    const pool = [];
    for (let i = first; i <= last; i++) if (!takenSet.has(i) && !selected.includes(i)) pool.push(i);
    if (pool.length < count) { toast.error("No hay suficientes boletos disponibles"); return; }
    const picked = [];
    for (let i = 0; i < count; i++) {
      const idx = Math.floor(Math.random() * pool.length);
      picked.push(pool.splice(idx, 1)[0]);
    }
    setSelected((prev) => [...prev, ...picked]);
  };

  const maquinita = (count) => {
    setSpinning(true);
    setTimeout(() => { pickRandom(count); setSpinning(false); toast.success(`${count} boletos de la suerte añadidos`); }, 900);
  };

  const start = page * perPage;
  const nums = [];
  for (let i = first + start; i <= Math.min(first + start + perPage - 1, last); i++) nums.push(i);
  const filtered = q ? nums.filter((n) => fmt(n).includes(q)) : nums;

  return (
    <div data-testid="ticket-selector">
      <div className="mb-4 flex gap-2 rounded-xl border border-slate-200 bg-white p-1.5">
        {[["manual", "Manual", Grid3x3], ["random", "Aleatorio", Shuffle], ["lucky", "Maquinita", Sparkles]].map(([m, l, Icon]) => (
          <button key={m} onClick={() => setMode(m)} data-testid={`selector-mode-${m}`}
            className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
              mode === m ? "bg-brand text-white" : "text-slate-500 hover:bg-slate-100"}`}>
            <Icon className="h-4 w-4" />{l}
          </button>
        ))}
      </div>

      {mode === "manual" && (
        <div>
          <div className="mb-3 flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar número..." className="pl-9" data-testid="ticket-search" />
            </div>
            <Button variant="outline" size="sm" onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}>Anterior</Button>
            <span className="font-mono text-xs text-slate-500">{fmt(first + start)}–{fmt(Math.min(first + start + perPage - 1, last))}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={start + perPage >= total}>Siguiente</Button>
          </div>
          <div className="grid grid-cols-5 gap-1.5 sm:grid-cols-10">
            {filtered.map((n) => {
              const isTaken = takenSet.has(n);
              const isSel = selected.includes(n);
              return (
                <button key={n} onClick={() => toggle(n)} disabled={isTaken} data-testid={`ticket-${n}`}
                  className={`rounded-lg py-2 font-mono text-xs font-semibold transition-all ${
                    isTaken ? "cursor-not-allowed bg-slate-100 text-slate-300 line-through"
                    : isSel ? "bg-brand text-white ticket-pop"
                    : "border border-slate-200 bg-white text-slate-600 hover:border-brand hover:text-brand"}`}>
                  {fmt(n)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {mode === "random" && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-center">
          <p className="text-sm text-slate-500">Indica cuántos boletos quieres y el sistema elegirá disponibles al azar.</p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {BLOCKS.map((b) => (
              <Button key={b} variant="outline" onClick={() => pickRandom(b)} data-testid={`random-${b}`} className="font-mono">+{b}</Button>
            ))}
          </div>
        </div>
      )}

      {mode === "lucky" && (
        <div className="rounded-xl border border-slate-200 bg-gradient-to-b from-slate-900 to-slate-800 p-8 text-center">
          <motion.div animate={spinning ? { rotate: 360 } : {}} transition={{ duration: 0.9, ease: "linear" }}>
            <Sparkles className="mx-auto h-10 w-10 text-amber-400" />
          </motion.div>
          <h4 className="mt-3 text-lg font-bold text-white">Maquinita de la Suerte</h4>
          <p className="mt-1 text-sm text-slate-300">Deja que la suerte elija tus números.</p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {BLOCKS.map((b) => (
              <Button key={b} disabled={spinning} onClick={() => maquinita(b)} data-testid={`lucky-${b}`}
                className="bg-white/10 font-mono text-white hover:bg-white/20">{b}</Button>
            ))}
          </div>
        </div>
      )}

      {selected.length > 0 && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">Seleccionados · <span className="font-mono text-brand">{num(selected.length)}</span></span>
            <button onClick={() => setSelected([])} className="text-xs text-red-500 hover:underline" data-testid="clear-selection">Limpiar todo</button>
          </div>
          <div className="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto">
            {selected.slice().sort((a, b) => a - b).map((n) => (
              <span key={n} className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-1 font-mono text-xs font-semibold text-brand">
                {fmt(n)}<X className="h-3 w-3 cursor-pointer" onClick={() => toggle(n)} />
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
